import {
  IAutocreditStatusTotal,
  IAutocreditAccount,
  IAutocreditAccountBody,
  IAutocreditAuthorizeBody,
  IAutocreditAuthorizeResponse,
  IAutocreditLedger,
  IPaginate,
  RootState,
  StoreState,
} from '@/types/types';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { isNetworkError } from '@/utils/helpers';
import { AutocreditService } from '@/services/autocredit.service';

const state: StoreState<IAutocreditLedger> & {
  ledgers: IAutocreditLedger[];
  ledgersPagination: IPaginate;
  ledgerTotals: IAutocreditStatusTotal[];
  accounts: IAutocreditAccount[];
  accountsPagination: IPaginate;
} = {
  all: [],
  reviewPendingPagination: {
    total: 0,
    itemsPerPage: 50,
    page: 1,
    pages: 0,
  },
  ledgers: [],
  ledgersPagination: {
    total: 0,
    itemsPerPage: 50,
    page: 1,
    pages: 0,
  },
  ledgerTotals: [],
  accounts: [],
  accountsPagination: {
    total: 0,
    itemsPerPage: 50,
    page: 1,
    pages: 0,
  },
};

type AutocreditState = typeof state;

const mutations: MutationTree<AutocreditState> = {
  UPDATE_REVIEW_PENDING_LIST(state, payload: Array<IAutocreditLedger>) {
    state.all = payload;
  },
  UPDATE_REVIEW_PENDING_PAGINATION(state, payload: IPaginate) {
    state.reviewPendingPagination = payload;
  },
  UPDATE_LEDGERS_LIST(state, payload: Array<IAutocreditLedger>) {
    state.ledgers = payload;
  },
  UPDATE_LEDGERS_PAGINATION(state, payload: IPaginate) {
    state.ledgersPagination = payload;
  },
  UPDATE_LEDGER_TOTALS(state, payload: Array<IAutocreditStatusTotal>) {
    state.ledgerTotals = payload;
  },
  UPDATE_ACCOUNTS_LIST(state, payload: Array<IAutocreditAccount>) {
    state.accounts = payload;
  },
  UPDATE_ACCOUNTS_PAGINATION(state, payload: IPaginate) {
    state.accountsPagination = payload;
  },
};

const actions: ActionTree<AutocreditState, RootState> = {
  async fetchLedgers({ dispatch, commit }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await AutocreditService.fetchLedgers(query);
      if (response$) {
        dispatch('isPageLoading', false, { root: true });
        const { data, meta } = response$.data;
        commit('UPDATE_LEDGERS_LIST', data);
        commit('UPDATE_LEDGER_TOTALS', meta.byStatus ?? []);
        commit('UPDATE_LEDGERS_PAGINATION', {
          page: meta.page,
          pages: meta.totalPages,
          total: meta.total,
          itemsPerPage: meta.limit,
        });
      }
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
      } else {
        dispatch('snackBarMessage', e.response?.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async resolveLedger(
    { dispatch },
    payload: {
      adminId: string;
      ledgerId: string;
      action: 'retry' | 'new-reference' | 'mark-posted';
      reason?: string;
      downstreamReference?: string;
      confirmedNotPaid?: boolean;
      query: string;
    },
  ) {
    try {
      dispatch('isLoading', true, { root: true });
      let message: string;
      if (payload.action === 'mark-posted') {
        await AutocreditService.markLedgerPosted(payload.adminId, payload.ledgerId, {
          downstreamReference: payload.downstreamReference as string,
          reason: payload.reason as string,
        });
        message = 'Entry recorded as posted — no payment was sent';
      } else {
        const response$ = await AutocreditService.reprocessLedger(payload.adminId, payload.ledgerId, {
          regenerateReference: payload.action === 'new-reference',
          reason: payload.reason,
          confirmedNotPaid: payload.confirmedNotPaid,
        });
        const { referenceRegenerated, referenceNo } = response$.data ?? {};
        message = referenceRegenerated
          ? `Queued for reprocessing with new reference ${referenceNo}`
          : 'Queued for reprocessing';
      }
      dispatch('isLoading', false, { root: true });
      dispatch('updateDialog', { idx: 'resolve', state: false }, { root: true });
      dispatch('snackBarMessage', message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchLedgers', payload.query);
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
      } else {
        dispatch('snackBarMessage', e.response?.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async fetchAccounts({ dispatch, commit }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await AutocreditService.fetchAccounts(query);
      if (response$) {
        dispatch('isPageLoading', false, { root: true });
        const { data, meta } = response$.data;
        commit('UPDATE_ACCOUNTS_LIST', data);
        commit('UPDATE_ACCOUNTS_PAGINATION', {
          page: meta.page,
          pages: meta.totalPages,
          total: meta.total,
          itemsPerPage: meta.limit,
        });
      }
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async upsertAccount({ dispatch }, payload: { body: IAutocreditAccountBody; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      await AutocreditService.upsertAccount(payload.body);
      dispatch('isLoading', false, { root: true });
      dispatch('updateDialog', { idx: 'add', state: false }, { root: true });
      dispatch('snackBarMessage', 'Bank details saved successfully', { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchAccounts', payload.query);
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
      } else {
        dispatch('snackBarMessage', e.response?.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async fetchReviewPendingList({ dispatch, commit }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await AutocreditService.fetchReviewPending(query);
      if (response$) {
        dispatch('isPageLoading', false, { root: true });
        const { data, meta } = response$.data;
        commit('UPDATE_REVIEW_PENDING_LIST', data);
        commit('UPDATE_REVIEW_PENDING_PAGINATION', {
          page: meta.page,
          pages: meta.totalPages,
          total: meta.total,
          itemsPerPage: meta.limit,
        });
      }
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async authorizeReviewPending(
    { dispatch },
    payload: { adminId: string; body: IAutocreditAuthorizeBody; query: string },
  ) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = await AutocreditService.authorizeReviewPending(payload.adminId, payload.body);
      const { modifiedCount, queuedJobs } = response$.data as IAutocreditAuthorizeResponse;
      dispatch('isLoading', false, { root: true });
      dispatch('updateDialog', { idx: 'authorize', state: false }, { root: true });
      dispatch(
        'snackBarMessage',
        `Authorized ${modifiedCount} ${modifiedCount === 1 ? 'entry' : 'entries'} for crediting (${
          queuedJobs.length
        } job${queuedJobs.length === 1 ? '' : 's'} queued)`,
        { root: true },
      );
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchReviewPendingList', payload.query);
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
      } else if (e.response?.status === 404) {
        dispatch('snackBarMessage', 'No matching review-pending entries were found', { root: true });
        dispatch('fetchReviewPendingList', payload.query);
      } else {
        dispatch('snackBarMessage', e.response?.data?.message, { root: true });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
};

const getters: GetterTree<AutocreditState, RootState> = {
  getReviewPendingList: state => state.all,
  getReviewPendingPagination: state => state.reviewPendingPagination,
  getLedgers: state => state.ledgers,
  getLedgersPagination: state => state.ledgersPagination,
  getLedgerTotals: state => state.ledgerTotals,
  getAccounts: state => state.accounts,
  getAccountsPagination: state => state.accountsPagination,
};

export const autocredit: Module<AutocreditState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
